import { useState, useRef } from 'react';
import { UserCog, X, Loader2, Lock } from 'lucide-react';
import { ASSIGNABLE_ROLES, type AssignableRole, type OrgUserWithRole } from '@property/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAllUserSearch, useUserPropertyCount } from '@/hooks/use-agent-queries';
import { useSwitchUserRole, ROLE_LABELS } from '@/hooks/use-user-role-mutations';

function UserSearchInput({
  selectedUser,
  onSelect,
  onClear,
}: {
  selectedUser: OrgUserWithRole | null;
  onSelect: (user: OrgUserWithRole) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const { data: results, isFetching } = useAllUserSearch(debouncedQuery);

  function handleChange(value: string) {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(value);
      setOpen(value.length >= 3);
    }, 300);
  }

  function handleSelect(user: OrgUserWithRole) {
    onSelect(user);
    setQuery('');
    setDebouncedQuery('');
    setOpen(false);
  }

  if (selectedUser) {
    const displayName = selectedUser.name ?? selectedUser.email;
    const initial = displayName.charAt(0).toUpperCase();
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium w-fit">
        <Avatar className="size-6 rounded-full shrink-0">
          <AvatarImage src={selectedUser.profilePictureUrl ?? undefined} alt={displayName} />
          <AvatarFallback className="text-[10px]">{initial}</AvatarFallback>
        </Avatar>
        <span>{displayName}</span>
        <span className="text-xs text-muted-foreground">({ROLE_LABELS[selectedUser.role]})</span>
        <button
          onClick={onClear}
          className="ml-1 rounded-full hover:bg-primary/20 p-0.5 transition-colors"
          aria-label="Clear user selection"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <Input
        placeholder="Search by name or email…"
        value={query}
        onChange={(e) => handleChange(e.target.value)}
        className="h-10"
      />
      {open && (
        <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md">
          {isFetching ? (
            <div className="flex items-center justify-center p-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : results && results.length > 0 ? (
            <ul className="max-h-52 overflow-y-auto py-1">
              {results.map((user) => {
                const isAdmin = user.role === 'admin';
                const displayName = user.name ?? user.email;
                const initial = displayName.charAt(0).toUpperCase();
                return (
                  <li key={user.id}>
                    <button
                      disabled={isAdmin}
                      onClick={() => !isAdmin && handleSelect(user)}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 enabled:hover:bg-accent"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Avatar className="size-7 rounded-full shrink-0">
                          <AvatarImage
                            src={user.profilePictureUrl ?? undefined}
                            alt={displayName}
                          />
                          <AvatarFallback className="text-[10px]">{initial}</AvatarFallback>
                        </Avatar>
                        <span className="flex flex-col items-start text-left min-w-0">
                          <span className="font-medium truncate">{displayName}</span>
                          {user.name && (
                            <span className="text-xs text-muted-foreground truncate">
                              {user.email}
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="flex items-center gap-1 shrink-0">
                        <span className="text-xs text-muted-foreground">
                          {ROLE_LABELS[user.role]}
                        </span>
                        {isAdmin && <Lock className="h-3 w-3 text-muted-foreground" />}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="p-3 text-sm text-muted-foreground">No users found.</p>
          )}
        </div>
      )}
    </div>
  );
}

export function AdminRoleSwitch() {
  const [selectedUser, setSelectedUser] = useState<OrgUserWithRole | null>(null);
  const [newRole, setNewRole] = useState<AssignableRole | ''>('');
  const { mutate: switchRole, isPending } = useSwitchUserRole();
  const { data: propertyCount, isLoading: isCountLoading } = useUserPropertyCount(
    selectedUser?.id ?? null,
  );

  const hasProperties = (propertyCount ?? 0) > 0;

  function handleSelectUser(user: OrgUserWithRole) {
    setSelectedUser(user);
    setNewRole('');
  }

  function handleClear() {
    setSelectedUser(null);
    setNewRole('');
  }

  function handleSave() {
    if (!selectedUser || !newRole || hasProperties) return;
    switchRole(
      { userId: selectedUser.id, role: newRole },
      {
        onSuccess: () => {
          setSelectedUser(null);
          setNewRole('');
        },
      },
    );
  }

  const selectableRoles = selectedUser
    ? ASSIGNABLE_ROLES.filter((r) => r !== selectedUser.role)
    : ASSIGNABLE_ROLES;

  return (
    <section className="rounded-xl border bg-card p-6 shadow-sm space-y-4">
      <div className="flex items-center gap-2">
        <UserCog className="h-5 w-5 text-muted-foreground" />
        <h2 className="font-display text-base font-medium text-foreground">Switch User Role</h2>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
        <div className="flex flex-col gap-1.5 flex-1 min-w-0">
          <span className="text-sm font-medium text-muted-foreground">User</span>
          <UserSearchInput
            selectedUser={selectedUser}
            onSelect={handleSelectUser}
            onClear={handleClear}
          />
        </div>

        {selectedUser && (
          <>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-muted-foreground">New role</span>
              <Select
                value={newRole}
                onValueChange={(v) => setNewRole(v as AssignableRole)}
                disabled={hasProperties}
              >
                <SelectTrigger className="w-48 h-10">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {selectableRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleSave}
              disabled={!newRole || isPending || hasProperties || isCountLoading}
              className="h-10 self-end"
              title={
                hasProperties
                  ? 'This user owns properties and cannot have their role changed'
                  : undefined
              }
            >
              {isPending || isCountLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isCountLoading ? 'Checking…' : 'Saving…'}
                </>
              ) : (
                'Save'
              )}
            </Button>
          </>
        )}
      </div>

      {selectedUser && hasProperties && !isCountLoading && (
        <p
          role="alert"
          className="flex items-center gap-2 text-sm text-muted-foreground border border-border rounded-lg px-4 py-3 bg-muted"
        >
          <Lock className="h-4 w-4 shrink-0 text-ring" />
          Role cannot be changed — this user owns{' '}
          {propertyCount === 1 ? '1 property' : `${propertyCount} properties`}. Transfer or archive
          their properties before reassigning their role.
        </p>
      )}
    </section>
  );
}
